import { useEffect, useState } from "react";
import { Button, Field, SelectField, StatusBadge } from "@leylion/design-system";
import {
  createPlatformIdempotencyKey,
  getPlatformRegistration,
  getPlatformRegistrations,
  reconcilePlatformProvisioning
} from "./platform-client";
import type {
  PlatformRegistrationDetail,
  PlatformRegistrationFilters,
  PlatformRegistrationSummary
} from "./platform-types";

type Language = "ar" | "en";
type Tone = "success" | "warning" | "danger" | "info" | "neutral";

function formatDate(value: string | null, language: Language): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(language === "ar" ? "ar-AE" : "en-AE", {
    dateStyle: "medium",
    timeStyle: "short"
  });
}

function tone(status: string | null): Tone {
  if (!status) return "neutral";
  if (["Completed", "Succeeded"].includes(status)) return "success";
  if (["WaitingVerification", "ReadyToProvision", "Provisioning", "Running"].includes(status)) return "info";
  if (["RetryableFailure", "Stuck", "FailedRetryable"].includes(status)) return "warning";
  if (["Rejected", "Blocked", "OnboardingBlocked", "FailedPermanent"].includes(status)) return "danger";
  return "neutral";
}

function label(value: string | null, language: Language): string {
  if (!value) return "—";
  const labels: Record<string, { ar: string; en: string }> = {
    Started: { ar: "بدأ التسجيل", en: "Started" },
    VerificationPending: { ar: "بانتظار التحقق", en: "Verification pending" },
    Verified: { ar: "تم التحقق", en: "Verified" },
    ReadyToProvision: { ar: "جاهز للتأسيس", en: "Ready to provision" },
    Provisioning: { ar: "قيد التأسيس", en: "Provisioning" },
    Completed: { ar: "مكتمل", en: "Completed" },
    Expired: { ar: "منتهي", en: "Expired" },
    Blocked: { ar: "محظور", en: "Blocked" },
    OnboardingBlocked: { ar: "الإعداد الأولي يحتاج انتباه", en: "Onboarding needs attention" },
    Cancelled: { ar: "ملغي", en: "Cancelled" },
    Running: { ar: "قيد التنفيذ", en: "Running" },
    Succeeded: { ar: "ناجح", en: "Succeeded" },
    FailedRetryable: { ar: "فشل قابل للإعادة", en: "Retryable failure" },
    FailedPermanent: { ar: "فشل نهائي", en: "Permanent failure" },
    WaitingVerification: { ar: "بانتظار التحقق", en: "Waiting for verification" },
    RetryableFailure: { ar: "فشل قابل للاستعادة", en: "Retryable failure" },
    Rejected: { ar: "طلب مرفوض", en: "Rejected" },
    Stuck: { ar: "عملية عالقة", en: "Stuck" },
    Terminal: { ar: "منتهي", en: "Terminal" },
    InProgress: { ar: "قيد الإعداد", en: "In progress" },
    NotStarted: { ar: "لم يبدأ", en: "Not started" }
  };
  return labels[value]?.[language] ?? value;
}

export function PlatformRegistrationControlCenter({
  token,
  language,
  initialRegistrationId,
  initialAttentionOnly
}: {
  token: string;
  language: Language;
  initialRegistrationId?: string;
  initialAttentionOnly?: boolean;
}) {
  const ar = language === "ar";
  const [items, setItems] = useState<PlatformRegistrationSummary[]>([]);
  const [filters, setFilters] = useState<PlatformRegistrationFilters>({ attentionOnly: initialAttentionOnly });
  const [selected, setSelected] = useState<PlatformRegistrationDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [recoveryReason, setRecoveryReason] = useState("STALE_ATTEMPT_REVIEW");
  const [recoveryNote, setRecoveryNote] = useState("");
  const [recoveryConfirmation, setRecoveryConfirmation] = useState("");
  const [recoveryNotice, setRecoveryNotice] = useState("");

  const load = async () => {
    setBusy(true);
    setError("");
    try {
      setItems(await getPlatformRegistrations(token, filters));
      setSelected(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Registration control request failed.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (initialRegistrationId) void open(initialRegistrationId);
    else void load();
  }, [token, initialRegistrationId]);

  const open = async (registrationId: string) => {
    setBusy(true);
    setError("");
    try {
      setSelected(await getPlatformRegistration(token, registrationId));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Registration detail request failed.");
    } finally {
      setBusy(false);
    }
  };

  const reconcile = async () => {
    if (!selected) return;
    const attempt = selected.attempts[0];
    if (!attempt || attempt.status !== "Running" || selected.registration.diagnosticState !== "Stuck") return;
    setRecoveryBusy(true);
    setError("");
    setRecoveryNotice("");
    try {
      const result = await reconcilePlatformProvisioning(
        token,
        selected.registration.signupIntentId,
        {
          expectedAttemptId: attempt.attemptId,
          expectedAttemptNumber: attempt.attemptNumber,
          reasonCode: recoveryReason,
          reasonNote: recoveryNote.trim(),
          confirmationPhrase: recoveryConfirmation.trim()
        },
        createPlatformIdempotencyKey("provisioning-recovery")
      );
      setRecoveryNotice(result.outcome === "ClientRetryReleased"
        ? (ar ? "تم تحرير المحاولة العالقة بأمان. يمكن للعميل إعادة المحاولة." : "The stuck attempt was safely released for client retry.")
        : (ar ? "تمت مطابقة المحاولة مع التأسيس الناجح دون إعادة إنشاء المؤسسة." : "The attempt was reconciled with committed success without reprovisioning."));
      setRecoveryNote("");
      setRecoveryConfirmation("");
      setSelected(await getPlatformRegistration(token, selected.registration.signupIntentId));
      setItems(await getPlatformRegistrations(token, filters));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Provisioning recovery failed.");
    } finally {
      setRecoveryBusy(false);
    }
  };

  if (selected) {
    const registration = selected.registration;
    const latestAttempt = selected.attempts[0] ?? null;
    const canReconcile = registration.diagnosticState === "Stuck" && latestAttempt?.status === "Running";
    const legacyAttemptHistory = selected.attempts.length === 0 && registration.provisioningAttemptCount > 0;
    return (
      <section className="platform-panel">
        <div className="platform-panel__toolbar">
          <div className="platform-panel__title">
            <div>
              <h2>{ar ? "تفاصيل التسجيل والتأسيس" : "Registration & provisioning detail"}</h2>
              <p>{registration.email}</p>
            </div>
          </div>
          <Button variant="secondary" type="button" onClick={() => void load()}>
            {ar ? "العودة" : "Back"}
          </Button>
        </div>
        {error ? <div className="platform-error" role="alert">{error}</div> : null}
        <div className="tenant360-subscription__grid">
          <Fact label={ar ? "حالة التسجيل" : "Signup status"} value={label(registration.signupStatus, language)} />
          <Fact label={ar ? "التشخيص" : "Diagnosis"} value={label(registration.diagnosticState, language)} />
          <Fact label={ar ? "حالة التأسيس" : "Provisioning status"} value={label(registration.provisioningStatus, language)} />
          <Fact label={ar ? "المؤسسة" : "Tenant"} value={registration.tenantId ?? "—"} />
          <Fact label={ar ? "الإعداد الأولي" : "Onboarding"} value={label(registration.onboardingStatus, language)} />
          <Fact label={ar ? "الإنجاز" : "Completion"} value={registration.onboardingCompletionPercent === null ? "—" : `${registration.onboardingCompletionPercent}%`} />
        </div>
        {canReconcile ? (
          <div className="platform-panel__toolbar">
            <div className="platform-panel__title">
              <div>
                <h3>{ar ? "تسوية العملية العالقة" : "Reconcile stuck provisioning"}</h3>
                <p>{ar ? "لا يعيد هذا الإجراء إنشاء المؤسسة. يفحص أولًا أثر النجاح ثم يحرر إعادة المحاولة فقط عندما يكون ذلك آمنًا." : "This does not reprovision the tenant. It proves committed success first and only releases client retry when safe."}</p>
              </div>
            </div>
            <div className="platform-directory-filters">
              <SelectField label={ar ? "سبب التسوية" : "Recovery reason"} value={recoveryReason} onChange={(event) => setRecoveryReason(event.target.value)}>
                <option value="STALE_ATTEMPT_REVIEW">{ar ? "مراجعة محاولة عالقة" : "Stale attempt review"}</option>
                <option value="POST_COMMIT_LEDGER_REPAIR">{ar ? "إصلاح سجل بعد نجاح التأسيس" : "Post-commit ledger repair"}</option>
                <option value="OPERATOR_DIAGNOSTIC_RECONCILIATION">{ar ? "تسوية تشخيصية" : "Operator diagnostic reconciliation"}</option>
              </SelectField>
              <Field label={ar ? "ملاحظة السبب" : "Reason note"} value={recoveryNote} onChange={(event) => setRecoveryNote(event.target.value)} placeholder={ar ? "لا تضع كلمات مرور أو رموزًا سرية" : "Do not enter passwords or secrets"} />
              <Field label={ar ? "عبارة التأكيد" : "Confirmation phrase"} value={recoveryConfirmation} onChange={(event) => setRecoveryConfirmation(event.target.value)} placeholder="RECONCILE STUCK PROVISIONING" />
              <Button type="button" disabled={recoveryBusy || !recoveryNote.trim() || recoveryConfirmation.trim() !== "RECONCILE STUCK PROVISIONING"} onClick={() => void reconcile()}>
                {recoveryBusy ? "…" : ar ? "تنفيذ التسوية" : "Reconcile"}
              </Button>
            </div>
          </div>
        ) : null}
        {recoveryNotice ? <div className="platform-success" role="status">{recoveryNotice}</div> : null}
        <div className="platform-table-wrap">
          <table className="platform-table">
            <thead><tr>
              <th>{ar ? "المحاولة" : "Attempt"}</th>
              <th>{ar ? "الحالة" : "Status"}</th>
              <th>{ar ? "الفشل" : "Failure"}</th>
              <th>{ar ? "بدأت" : "Started"}</th>
              <th>{ar ? "اكتملت" : "Completed"}</th>
            </tr></thead>
            <tbody>
              {legacyAttemptHistory ? (
                <tr>
                  <td colSpan={5}>
                    <strong>{ar
                      ? `سجل تاريخي: ${registration.provisioningAttemptCount} محاولة قبل تفعيل سجل المحاولات الدائم.`
                      : `Legacy history: ${registration.provisioningAttemptCount} attempt(s) predate the durable attempt ledger.`}</strong>
                    <small>{ar
                      ? "لا تتوفر تفاصيل توقيت المحاولة التاريخية في السجل الدائم، ولم يتم إنشاء سجل بديل مصطنع."
                      : "This legacy operation does not have durable attempt timestamps; no synthetic attempt row was created."}</small>
                  </td>
                </tr>
              ) : selected.attempts.map((attempt) => (
                <tr key={attempt.attemptId}>
                  <td>#{attempt.attemptNumber}</td>
                  <td><StatusBadge tone={tone(attempt.status)}>{label(attempt.status, language)}</StatusBadge></td>
                  <td><strong>{attempt.failureCode ?? "—"}</strong><small>{attempt.failureDetailRedacted ?? ""}</small></td>
                  <td>{formatDate(attempt.startedAt, language)}</td>
                  <td>{formatDate(attempt.completedAt, language)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {selected.recoveryHistory.length > 0 ? (
          <div className="platform-table-wrap">
            <table className="platform-table">
              <thead><tr>
                <th>{ar ? "سجل الاستعادة" : "Recovery history"}</th>
                <th>{ar ? "النتيجة" : "Outcome"}</th>
                <th>{ar ? "السبب" : "Reason"}</th>
                <th>{ar ? "المنفذ" : "Actor"}</th>
                <th>{ar ? "الوقت" : "Time"}</th>
              </tr></thead>
              <tbody>{selected.recoveryHistory.map((entry) => (
                <tr key={entry.auditEventId}>
                  <td><strong>#{entry.attemptNumber}</strong><small>{entry.attemptId}</small></td>
                  <td>{entry.outcome}</td>
                  <td><strong>{entry.reasonCode}</strong><small>{entry.reasonNote}</small></td>
                  <td>{entry.actorUserId ?? "—"}</td>
                  <td>{formatDate(entry.occurredAt, language)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <section className="platform-panel">
      <div className="platform-panel__toolbar platform-panel__toolbar--directory">
        <div className="platform-panel__title">
          <div>
            <h2>{ar ? "مركز التسجيل والتأسيس" : "Registration & provisioning control center"}</h2>
            <p>{ar ? "رحلة التسجيل والتأسيس والمحاولات والإعداد الأولي من Control Plane فقط." : "Registration, provisioning attempts and onboarding from the control plane only."}</p>
          </div>
        </div>
        <div className="platform-directory-filters">
          <Field
            label={ar ? "بحث" : "Search"}
            value={filters.search ?? ""}
            onChange={(event) => setFilters((value) => ({ ...value, search: event.target.value }))}
            placeholder={ar ? "البريد أو Registration ID" : "Email or registration ID"}
          />
          <SelectField
            label={ar ? "حالة التسجيل" : "Signup status"}
            value={filters.signupStatus ?? ""}
            onChange={(event) => setFilters((value) => ({ ...value, signupStatus: event.target.value }))}
          >
            <option value="">{ar ? "الكل" : "All"}</option>
            <option value="VerificationPending">{ar ? "بانتظار التحقق" : "Verification pending"}</option>
            <option value="Verified">{ar ? "تم التحقق" : "Verified"}</option>
            <option value="ReadyToProvision">{ar ? "جاهز للتأسيس" : "Ready to provision"}</option>
            <option value="Provisioning">{ar ? "قيد التأسيس" : "Provisioning"}</option>
            <option value="Completed">{ar ? "مكتمل" : "Completed"}</option>
            <option value="Blocked">{ar ? "محظور" : "Blocked"}</option>
            <option value="Expired">{ar ? "منتهي" : "Expired"}</option>
          </SelectField>
          <label className="platform-admin-confirm">
            <input
              type="checkbox"
              checked={Boolean(filters.attentionOnly)}
              onChange={(event) => setFilters((value) => ({ ...value, attentionOnly: event.target.checked }))}
            />
            <span>{ar ? "تحتاج انتباه فقط" : "Needs attention only"}</span>
          </label>
          <Button type="button" disabled={busy} onClick={() => void load()}>{busy ? "…" : ar ? "تطبيق" : "Apply"}</Button>
        </div>
      </div>
      {error ? <div className="platform-error" role="alert">{error}</div> : null}
      <div className="platform-table-wrap">
        <table className="platform-table">
          <thead><tr>
            <th>{ar ? "التسجيل" : "Registration"}</th>
            <th>{ar ? "التشخيص" : "Diagnosis"}</th>
            <th>{ar ? "التأسيس" : "Provisioning"}</th>
            <th>{ar ? "المحاولات" : "Attempts"}</th>
            <th>{ar ? "الإعداد" : "Onboarding"}</th>
            <th>{ar ? "الفشل" : "Failure"}</th>
            <th>{ar ? "آخر نشاط" : "Last activity"}</th>
          </tr></thead>
          <tbody>{items.map((item) => {
            const legacyAttemptHistory = item.latestAttempt === null && item.provisioningAttemptCount > 0;
            return (
              <tr key={item.signupIntentId} className="platform-table__clickable" onClick={() => void open(item.signupIntentId)}>
                <td><strong>{item.email}</strong><small>{label(item.signupStatus, language)} · {item.signupIntentId}</small></td>
                <td><StatusBadge tone={tone(item.diagnosticState)}>{label(item.diagnosticState, language)}</StatusBadge></td>
                <td><span>{label(item.provisioningStatus, language)}</span><small>{item.tenantId ?? "—"}</small></td>
                <td><span>{item.provisioningAttemptCount}</span>{legacyAttemptHistory ? <small>{ar ? "تاريخي قبل سجل المحاولات" : "Legacy, pre-ledger"}</small> : null}</td>
                <td><span>{label(item.onboardingStatus, language)}</span><small>{item.onboardingCompletionPercent === null ? "" : `${item.onboardingCompletionPercent}%`}</small></td>
                <td><strong>{item.failureCode ?? "—"}</strong><small>{item.failureDetailRedacted ?? ""}</small></td>
                <td>{formatDate(item.lastActivityAt, language)}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
    </section>
  );
}

function Fact({ label: factLabel, value }: { label: string; value: string }) {
  return <div className="tenant360-fact"><span>{factLabel}</span><strong>{value || "—"}</strong></div>;
}
